import React from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles, Paper, Tooltip, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import BookmarkAddOutlinedIcon from '@material-ui/icons/BookmarkBorder'
import { useChartCatalogActions } from '../hooks/useChartCatalogActions'
import { TOOL_REGISTRY } from './tools/toolRegistry'

// Instance-model note: in the v8+ instance model, a tile's identity
// IS its `tile.id` (the ChartInstance id). The catalog preset that
// created it is metadata only -- duplicates of the same preset have
// different ids and remove independently.

// Tile chrome for one chart in the stacked panel. Owns the header
// (title + save/remove/close); body renders the tool with its payload.

const useStyles = makeStyles(() => ({
  tile: {
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--opal-surface-default, #fff)',
    border: '1px solid var(--opal-border-default, #eee)',
    borderRadius: 'var(--border-radius-md, 6px)',
    overflow: 'hidden',
    // Critical: without this, the tile (a flex child of the stack) is a
    // scroll container due to overflow:hidden, which makes its automatic
    // min-height resolve to 0 -- the stack happily shrinks it past the
    // body's min-height: 280, clipping the chart. flexShrink: 0 keeps
    // each tile at its natural content height; ChartStack scrolls.
    flexShrink: 0
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px 6px 10px',
    borderBottom: '1px solid var(--opal-border-default, #eee)',
    background: 'var(--theme-color-surface-d1, #fafafa)',
    minHeight: 36
  },
  headerIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    color: 'var(--color-neutral-700, #555)',
    flexShrink: 0
  },
  headerTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 13,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-800, #333)'
  },
  actions: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0
  },
  actionBtn: {
    padding: 4,
    color: 'var(--color-neutral-600, #666)',
    '&:hover': {
      color: 'var(--color-neutral-900, #222)'
    }
  },
  actionIcon: {
    fontSize: 16
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    // Guarantees a visible chart area even when the tool renders an
    // empty / loading / error placeholder, so tiles don't collapse to
    // just their header.
    minHeight: 280
  },
  placeholder: {
    padding: '24px 16px',
    textAlign: 'center',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13,
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  placeholderHint: {
    fontSize: 11,
    color: 'var(--color-neutral-400, #9ca3af)'
  }
}))

export default function ChartTile ({ tile, onClose, onRemove, onPayloadChange, displayTitle, renderIcon, placeholder }) {
  const classes = useStyles()
  const { addChart } = useChartCatalogActions()
  const tool = TOOL_REGISTRY[tile?.toolId] || null
  const ToolComponent = tool?.component || null
  const titleFromTool = tool?.label || 'Chart'
  const title = displayTitle || titleFromTool
  // Saved tiles run in compact mode: just the chart, no detail table.
  // Right-click (transient) tiles render full detail so the user can
  // drill in immediately. See DEVELOPMENT_NOTES section 24.
  const compact = tile?.source === 'saved'

  // "Save to view" only shows on transient tiles that originated from
  // a catalog preset. Duplicates are fine in the instance model, so
  // there's no "already saved" gate -- the user can save the same
  // preset multiple times, each becoming its own instance.
  const canSaveToView = tile?.source === 'transient' && !!tile?.catalogId

  const handleSave = () => {
    if (!canSaveToView) return
    addChart({ catalogId: tile.catalogId, toolId: tile.toolId })
    onClose?.()
  }

  return (
    <Paper className={classes.tile} elevation={0} square={false}>
      <div className={classes.header}>
        {renderIcon && (
          <span className={classes.headerIcon} aria-hidden='true'>{renderIcon()}</span>
        )}
        <Typography component='div' className={classes.headerTitle} title={title}>
          {title}
        </Typography>
        <span className={classes.actions}>
          {canSaveToView && (
            <Tooltip title='Save to view' placement='top' enterDelay={400}>
              <IconButton size='small' className={classes.actionBtn} onClick={handleSave} aria-label='Save chart to view'>
                <BookmarkAddOutlinedIcon className={classes.actionIcon} />
              </IconButton>
            </Tooltip>
          )}
          {tile?.source === 'saved' && onRemove && (
            <Tooltip title='Remove from view' placement='top' enterDelay={400}>
              <IconButton size='small' className={classes.actionBtn} onClick={onRemove} aria-label='Remove chart from view'>
                <DeleteOutlineIcon className={classes.actionIcon} />
              </IconButton>
            </Tooltip>
          )}
          {tile?.source === 'transient' && onClose && (
            <Tooltip title='Close chart' placement='top' enterDelay={400}>
              <IconButton size='small' className={classes.actionBtn} onClick={onClose} aria-label='Close chart'>
                <CloseIcon className={classes.actionIcon} />
              </IconButton>
            </Tooltip>
          )}
        </span>
      </div>
      <div className={classes.body}>
        {placeholder ? (
          <div className={classes.placeholder}>
            <span>{placeholder}</span>
            {tile?.source === 'saved' && (
              <span className={classes.placeholderHint}>
                Add the missing column to your view or remove this chart from the view.
              </span>
            )}
          </div>
        ) : (
          ToolComponent && (
            <ToolComponent
              payload={tile?.payload}
              compact={compact}
              onPayloadChange={onPayloadChange}
            />
          )
        )}
      </div>
    </Paper>
  )
}

ChartTile.propTypes = {
  tile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    source: PropTypes.oneOf(['saved', 'transient']).isRequired,
    toolId: PropTypes.string,
    payload: PropTypes.object,
    catalogId: PropTypes.string
  }).isRequired,
  displayTitle: PropTypes.string,
  renderIcon: PropTypes.func,
  placeholder: PropTypes.string,
  onClose: PropTypes.func,
  onRemove: PropTypes.func,
  // (patch: object) -> void. Tools call this to persist user edits
  // (chart type, picked measure, axes, etc.) into the instance's
  // payload. Null for transient tiles (no persistence target).
  onPayloadChange: PropTypes.func
}
